import Axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse, type CancelTokenSource } from 'axios';
import jwtDecode from 'jwt-decode';

import {
	API_URL_SUMMARY_LAMBDAS,
	AUTH0_LOGIN_REDIRECT,
	getNavinaAuth0AuthURL,
	IS_PRODUCTION, // oxlint-disable-line navina/restricted-syntax -- legacy env-name check; gate new features on a dedicated VITE__ flag instead (see .cursor/rules/environment.mdc)
} from '../config/Environment';
import { type AuthStore, getOrCreateAuthStore } from '../stores/authStore'; // oxlint-disable-line import/no-cycle -- Pre-existing dependency cycle, grandfathered during the oxlint migration.
import { NEXT_URL_QUERY_PARAMS } from '../utils/consts';
import { getOrCreateApiSessionsServer } from './apiSessions'; // oxlint-disable-line import/no-cycle -- Pre-existing dependency cycle, grandfathered during the oxlint migration.
import { camelCaseResponse } from './camelCaseResponse';
import { createAxiosInstance, getErrorHandler, getSuccessHandler, type WithAxiosServer } from './createAxiosInstance'; // oxlint-disable-line import/no-cycle -- Pre-existing dependency cycle, grandfathered during the oxlint migration.

import type { GetClinicsDepartmentsProvidersResponsePayload } from '../components/AppointmentsSchedule/models/types';
import type { ProgramYearSegment } from '../components/DxSummary/models/ProgramYearModel.types';
import type { DiagnosesPushRequest, DiagnosesPushRequestEpic } from '../modules/push2emr/types';
import type {
	GetAppointmentDetailsResponse,
	GetDxCaptureDataResponse,
	IcdDataDB,
	SnomedFromIcdApiResult,
	QuicksightEmbedSettings,
	GetEpicTokenAndAppContextResponse,
	AscvdData,
	PreventivesType,
} from '../types';
import type { GetSummaryNotesResponse, HccVersions } from '@dxcapture/core';
import type {
	CorePushResult,
	AdjacentAppointmentsRequestPayload,
	AdjacentAppointmentsResponsePayload,
	PatientDemographicsRequestPayload,
	PatientDemographicsResponsePayload,
	PatientLookupRequestPayload,
	PatientLookupResponsePayload,
	PatientSelectorRequestPayload,
	PatientSelectorResponsePayload,
	SchedulePageSearchRequestPayload,
	SchedulePageSearchResponsePayload,
	SelectedGroupPracticeRequestPayload,
	SelectedGroupPracticeResponsePayload,
	NID,
	GetAthenaAppointmentDetailsRequestPayload,
	GetAthenaAppointmentDetailsResponsePayload,
} from '@navina/api-types';

const CancellationTokenCreator = Axios.CancelToken;

const QA_BRANCH = (import.meta.env.VITE__AWS_BRANCH || '').startsWith('qa/') ? import.meta.env.VITE__AWS_BRANCH : '';

const TOKEN_EXPIRATION_BUFFER_DURATION_SECONDS = 60;

const WITH_QA_BRANCH_AND_MAYBE_CACHE = { qaBranch: QA_BRANCH, useCache: IS_PRODUCTION } as const;

interface AuthHeaders {
	readonly token?: string;
	readonly authorizationToken?: string;
	readonly type?: string;
}

export interface AuthInfo {
	readonly headers?: AuthHeaders;
	readonly path: string;
	readonly isValid: boolean;
	readonly isRefresh?: boolean;
}

export interface GetDxCaptureDataConfig {
	readonly isGroupingOn: boolean;
	readonly shouldUseNewSuggestionsApi?: boolean;
}

function extractAscvdDataFromPreventive(preventive: PreventivesType | AscvdData | undefined): AscvdData | undefined {
	if (!preventive) {
		return undefined;
	}

	if ('ascvdData' in preventive) {
		return preventive.ascvdData;
	}

	return preventive;
}

export class ApiGatewayServer implements WithAxiosServer {
	readonly server: AxiosInstance;
	readonly authStore: AuthStore;

	constructor(baseUrl: string = API_URL_SUMMARY_LAMBDAS) {
		this.server = createAxiosInstance(baseUrl);
		this.authStore = getOrCreateAuthStore();
	}

	// eslint-disable-next-line navina/restricted-syntax -- Legacy codes inherited from the previous codebase
	protected getAuthInfo(path: string, token: string): AuthInfo {
		if (!token) {
			return { path, isValid: false };
		}

		if (this.authStore.getIsSAML) {
			const tokenPayload = jwtDecode<{ exp: number }>(token);
			const tokenExpirationUnixTimestamp = tokenPayload.exp;

			const tokenExpirationTimeInMilliseconds = tokenExpirationUnixTimestamp * 1_000;
			const bufferDurationInMilliseconds = TOKEN_EXPIRATION_BUFFER_DURATION_SECONDS * 1_000;

			const currentTimeInMilliseconds = Date.now();

			const isTokenExpired =
				tokenExpirationTimeInMilliseconds - bufferDurationInMilliseconds < currentTimeInMilliseconds;

			if (isTokenExpired) {
				const currentUrl = globalThis.location.href;
				console.log('Token about to expire, redirecting to Auth0 to get new token', {
					path,
					currentUrl,
				});
				// We need double encoding since it is decoded twice before used in the url
				const nextUrl = encodeURIComponent(
					encodeURIComponent(`${globalThis.location.pathname}${globalThis.location.search}`),
				);

				console.log('Redirecting to Auth0 to get new token', { nextUrl, inputPath: path });

				const fullNextUrl = getNavinaAuth0AuthURL(`${AUTH0_LOGIN_REDIRECT}?${NEXT_URL_QUERY_PARAMS}=${nextUrl}`);

				globalThis.location.href = fullNextUrl;

				return { path, isRefresh: true, isValid: false };
			}

			return {
				headers: {
					authorizationToken: `Bearer ${token}`,
					type: 'TOKEN',
				},
				path: `v2/${path}`,
				isValid: true,
			};
		}

		return { headers: { token }, path, isValid: true };
	}

	protected readonly baseRequest = async <TInput extends { [key: string]: any }, TOutput>(
		path: string,
		data?: TInput,
		cancelToken?: CancelTokenSource,
	): Promise<null | AxiosResponse<TOutput>> => {
		const authInfo = this.getAuthInfo(path, this.authStore.getToken);

		if (authInfo.isRefresh) {
			return null;
		}

		if (authInfo.isValid) {
			const axiosRequestConfig: AxiosRequestConfig = {
				headers: {
					'content-type': 'application/json',
					...authInfo.headers,
				},
				...(cancelToken && { cancelToken: cancelToken.token }),
			};

			const requestPromise: Promise<AxiosResponse<TOutput>> = this.server.post(
				authInfo.path,
				JSON.stringify(data),
				axiosRequestConfig,
			);

			const awaitedResponse = await requestPromise;

			const camelCasedResponse = {
				...awaitedResponse,
				// @ts-expect-error - Legacy code, nullability not yet enforced
				data: camelCaseResponse<TOutput>(awaitedResponse.data),
			};

			return camelCasedResponse;
		}

		this.authStore.reset();
		return null;
	};

	readonly getFileURL = async (
		fileId: string,
		overrideEnv?: string,
	): Promise<null | AxiosResponse<{
		readonly message: {
			readonly url: string;
			readonly isCopyable: boolean;
			readonly filename: string;
			readonly tryGetCopyable: boolean;
			readonly copyableFilePath: string;
			readonly isNewCopyableFlag: boolean;
		};
	}>> => {
		const authInfo = this.getAuthInfo('getFileURL', this.authStore.getToken);

		if (authInfo.isRefresh) {
			return null;
		}

		if (authInfo.isValid) {
			return this.server.post(authInfo.path, JSON.stringify({ id: fileId, overrideEnv }), {
				headers: { 'content-type': 'application/json', ...authInfo.headers },
			});
		}

		this.authStore.reset();
		return null;
	};

	// eslint-disable-next-line @typescript-eslint/unbound-method -- Works fine
	readonly downloadFile = Axios.get;

	readonly getClinicsDepartmentsProviders =
		// @ts-expect-error - Legacy code, nullability not yet enforced
		async (): Promise<null | AxiosResponse<GetClinicsDepartmentsProvidersResponsePayload>> => {
			const authInfo = this.getAuthInfo('getClinicsDepartmentsProviders', this.authStore.getToken);
			if (authInfo.isRefresh) {
				return null;
			}

			if (authInfo.isValid) {
				return this.server.post(
					authInfo.path,
					{},
					{ headers: { 'content-type': 'application/json', ...authInfo.headers } },
				);
			}
		};

	// @ts-expect-error - Legacy code, nullability not yet enforced
	readonly getUserClinicsHierarchy = async (): Promise<null | AxiosResponse> => {
		const authInfo = this.getAuthInfo('getUserClinicsHierarchy', this.authStore.getToken);

		if (authInfo.isRefresh) {
			return null;
		}

		if (authInfo.isValid) {
			return this.server.post(
				authInfo.path,
				{},
				{
					headers: { 'content-type': 'application/json', ...authInfo.headers },
				},
			);
		}
	};

	readonly getDxCaptureData = async (
		summaryId: string,
		{ isGroupingOn }: GetDxCaptureDataConfig = { isGroupingOn: false },
	): Promise<null | AxiosResponse<GetDxCaptureDataResponse>> => {
		const authInfo = this.getAuthInfo('getDxCaptureData', this.authStore.getToken);

		if (authInfo.isRefresh) {
			return null;
		}

		if (authInfo.isValid) {
			return this.server.post<GetDxCaptureDataResponse>(
				authInfo.path,
				JSON.stringify({
					...WITH_QA_BRANCH_AND_MAYBE_CACHE,
					sid: summaryId,
					isGroupingOn,
				}),
				{
					headers: { 'content-type': 'application/json', ...authInfo.headers },
				},
			);
		}

		console.warn('getDxCaptureData, token is null, resetting authStore');
		this.authStore.reset();
		return null;
	};

	readonly getAppointmentDetails = async (summaryId: string) => {
		const { isAdmin } = this.authStore;
		const urlParam = new URLSearchParams(globalThis.location.search);
		const isSkipValidationParamExists = urlParam.has('skipValidation');

		const response = await this.baseRequest<
			{ readonly summaryId: string; readonly useCache: boolean; readonly skipValidation?: boolean },
			GetAppointmentDetailsResponse
		>('getAppointmentDetails', {
			summaryId,
			useCache: false,
			skipValidation: isAdmin && isSkipValidationParamExists,
		});

		if (!response) {
			console.error('Failed to fetch appointment details. Response is null or undefined.');
			throw new Error('Failed to fetch appointment details');
		}

		// HACK: preventive is sometimes a full object with ascvdData and sometimes
		// is already the ascvdData payload itself. To keep the rest of the code using
		// a stable response.data.ascvdData shape, we detect which variant we got:
		// if preventive.ascvdData exists, use that; otherwise treat preventive
		// as the ascvdData value and normalize it into response.data.ascvdData.

		const preventive = response.data?.preventive;
		const ascvdData = extractAscvdDataFromPreventive(preventive);

		return {
			...response,
			data: {
				...response.data,
				ascvdData,
			},
		};
	};
	readonly getAppointmentDetailsByNid = async (nid: NID) => {
		const { isAdmin } = this.authStore;
		const urlParam = new URLSearchParams(globalThis.location.search);
		const isSkipValidationParamExists = urlParam.has('skipValidation');

		const response = await this.baseRequest<
			{ readonly nid: NID; readonly useCache: boolean; readonly skipValidation?: boolean },
			GetAppointmentDetailsResponse
		>('getAppointmentDetailsByNid', {
			nid,
			useCache: false,
			skipValidation: isAdmin && isSkipValidationParamExists,
		});

		if (!response) {
			console.error('Failed to fetch appointment details. Response is null or undefined.');
			throw new Error('Failed to fetch appointment details');
		}

		return response;
	};

	readonly getSummaryNotificationsBySummaryId = async (summaryId: string): Promise<null | AxiosResponse> => {
		const authInfo = this.getAuthInfo('getSummaryNotifications', this.authStore.getToken);

		if (authInfo.isRefresh) {
			return null;
		}

		if (authInfo.isValid) {
			return this.server.post(authInfo.path, JSON.stringify({ id: summaryId }), {
				headers: { 'content-type': 'application/json', ...authInfo.headers },
			});
		}

		console.warn('GetSummaryNotifications, token is null, resetting authStore');
		this.authStore.reset();
		return null;
	};

	readonly getEpicTokenAndAppContext = async (input: {
		code: string;
		connectionName: string;
		dataSourceId: string | string[] | null;
		redirectUri?: string;
	}): Promise<null | AxiosResponse<GetEpicTokenAndAppContextResponse>> => {
		const authInfo = this.getAuthInfo('getEpicTokenAndAppContext', this.authStore.getToken);

		if (authInfo.isRefresh) {
			return null;
		}

		if (authInfo.isValid) {
			return this.server.post(authInfo.path, JSON.stringify(input), {
				headers: { 'content-type': 'application/json', ...authInfo.headers },
			});
		}

		console.warn('GetSummaryNotifications, token is null, resetting authStore');
		this.authStore.reset();
		return null;
	};

	searchInOpenSearchCancelToken: CancelTokenSource;

	readonly searchInDocs = async (
		searchTerm: string,
		NID: string,
		dataSourceId: number,
	): Promise<null | AxiosResponse> => {
		this.searchInOpenSearchCancelToken = CancellationTokenCreator.source();

		const authInfo = this.getAuthInfo('searchInOpenSearch', this.authStore.getToken);
		if (authInfo.isRefresh) {
			return null;
		}

		if (authInfo.isValid) {
			return this.server.post(
				authInfo.path,
				{ searchTerm, NID, dataSourceId },
				{
					headers: { 'content-type': 'application/json', ...authInfo.headers },
					cancelToken: this.searchInOpenSearchCancelToken.token,
				},
			);
		}

		this.authStore.reset();
		return null;
	};

	readonly updateUserConfig = async (extraData: unknown): Promise<null | AxiosResponse> => {
		const authInfo = this.getAuthInfo('updateUserConfig', this.authStore.getToken);

		if (authInfo.isRefresh) {
			return null;
		}

		if (authInfo.isValid) {
			return this.server.post(
				authInfo.path,
				{ config: extraData },
				{ headers: { 'content-type': 'application/json', ...authInfo.headers } },
			);
		}

		this.authStore.reset();
		return null;
	};

	readonly getUserConfig = async (): Promise<null | AxiosResponse> => {
		const authInfo = this.getAuthInfo('getUserConfig', this.authStore.getToken);

		if (authInfo.isRefresh) {
			return null;
		}

		if (authInfo.isValid) {
			return this.server.post(authInfo.path, null, {
				headers: { 'content-type': 'application/json', ...authInfo.headers },
			});
		}

		this.authStore.reset();
		return null;
	};

	readonly passwordResetStartedReport = async (username: string, is180DaysReset = false): Promise<AxiosResponse> => {
		return this.server.post(
			'userResetPasswordAttempt',
			{ username, is180DaysReset },
			{ headers: { 'content-type': 'application/json' } },
		);
	};

	readonly getICDData = async (
		gender: string,
		dateOfBirth: string,
		riskModelConfiguration: ReadonlyArray<HccVersions>,
		searchText = '',
	): Promise<IcdDataDB[]> => {
		const authInfo = this.getAuthInfo('getICDData', this.authStore.getToken);

		if (authInfo.isRefresh) {
			// @ts-expect-error - Legacy code, nullability not yet enforced
			return null;
		}

		if (!authInfo.isValid) {
			console.warn('getICDData, token is null, resetting authStore');
			this.authStore.reset();
			return [];
		}

		console.log(`search: server search with prefix ${searchText}`);

		try {
			const response = await this.server.post(
				authInfo.path,
				JSON.stringify({ searchText, gender, date_of_birth: dateOfBirth, riskModelConfiguration }),
				{
					headers: { 'content-type': 'application/json', ...authInfo.headers },
				},
			);

			if (!response?.data?.message) {
				console.warn('An Error occurred', response);
				return [];
			}

			return response.data.message;
		} catch (err) {
			console.warn('An Error occurred', err);
			return [];
		}
	};

	readonly getNoteTemplates = async (dataSource: number) => {
		const authInfo = this.getAuthInfo('getNoteTemplates', this.authStore.getToken);

		if (authInfo.isRefresh) {
			return null;
		}

		if (!authInfo.isValid) {
			console.warn('getNoteTemplates, token is null, resetting authStore');
			this.authStore.reset();
			return [];
		}

		try {
			const response = await this.server.post(authInfo.path, JSON.stringify({ dataSource }), {
				headers: { 'content-type': 'application/json', ...authInfo.headers },
			});

			if (!response?.data?.message) {
				console.warn('An Error occurred', response);
				return [];
			}

			return response.data.message;
		} catch (err) {
			console.warn('An Error occurred', err);
			return [];
		}
	};

	readonly getSnomedFromICD = async (icdCode = ''): Promise<SnomedFromIcdApiResult | null> => {
		const authInfo = this.getAuthInfo('getSnomedFromICD', this.authStore.getToken);

		if (authInfo.isRefresh) {
			return null;
		}

		if (!authInfo.isValid) {
			console.warn('getSnomedFromICD, token is null, resetting authStore');
			this.authStore.reset();
			return null;
		}

		console.log(`getSnomedToICD for ICD code ${icdCode}`);

		try {
			const response = await this.server.post(authInfo.path, JSON.stringify({ icdCode }), {
				headers: { 'content-type': 'application/json', ...authInfo.headers },
			});

			if (!response?.data?.message) {
				console.warn('An Error occurred', response);
				return null;
			}

			return response.data.message;
		} catch (err) {
			console.warn('An Error occurred', err);
			return null;
		}
	};

	readonly addSummaryNote = async (sid: string, note: string) => {
		const authInfo = this.getAuthInfo('addSummaryNote', this.authStore.getToken);

		if (authInfo.isRefresh) {
			return null;
		}

		if (!authInfo.isValid) {
			console.warn('addSummaryNote, token is null, resetting authStore');
			this.authStore.reset();
			return null;
		}

		const response = await this.server.post<unknown[]>(authInfo.path, JSON.stringify({ sid, note }), {
			headers: { 'content-type': 'application/json', ...authInfo.headers },
		});

		return response.data;
	};

	readonly getSummaryNotes = async (sid: string): Promise<GetSummaryNotesResponse | null> => {
		const authInfo = this.getAuthInfo('getSummaryNotes', this.authStore.getToken);

		if (authInfo.isRefresh) {
			Promise.resolve(null);
		}

		if (!authInfo.isValid) {
			console.warn('getSummaryNotes, token is null, resetting authStore');
			this.authStore.reset();
			Promise.resolve(null);
		}

		const response = await this.server.post<GetSummaryNotesResponse>(authInfo.path, JSON.stringify({ sid }), {
			headers: { 'content-type': 'application/json', ...authInfo.headers },
		});

		return response.data;
	};

	readonly apiPush = async (
		payload: DiagnosesPushRequest | DiagnosesPushRequestEpic,
	): Promise<CorePushResult[] | null> => {
		const authInfo = this.getAuthInfo(`push2emrApi`, this.authStore.getToken);

		if (authInfo.isRefresh) {
			Promise.resolve(null);
		}

		if (!authInfo.isValid) {
			console.warn('apiPush, token is null, resetting authStore');
			this.authStore.reset();
			Promise.resolve(null);
		}

		const response = await this.server.post<CorePushResult[]>(authInfo.path, JSON.stringify({ ...payload }), {
			headers: { 'content-type': 'application/json', ...authInfo.headers },
		});

		return response.data;
	};

	readonly addSummaryInsightsAgilonNonDxActions = async (
		nid: string,
		sid: string,
		agilonSuggestionId: string,
		navinaSuggestionId: string,
		userSelection: string,
		updatedAt: string,
		reportingYear?: number,
		reportingSegment?: ProgramYearSegment,
	): Promise<null | undefined> => {
		const authInfo = this.getAuthInfo('addSummaryInsightsAgilonNonDxActions', this.authStore.getToken);

		if (authInfo.isRefresh) {
			return null;
		}

		if (!authInfo.isValid) {
			console.warn('addSummaryInsightsAgilonNonDxActions, token is null, resetting authStore');
			this.authStore.reset();
		}

		try {
			const response = await this.server.post(
				authInfo.path,
				{
					nid,
					sid,
					agilonSuggestionId,
					navinaSuggestionId,
					status: userSelection,
					updatedAt,
					reportingYear,
					reportingSegment,
				},
				{ headers: { 'content-type': 'application/json', ...authInfo.headers } },
			);

			if (response?.status !== 200) {
				console.warn('An Error occurred', response);
			}
		} catch (err) {
			console.warn('An Error occurred', err);
		}
	};

	readonly getQuicksightEmbedURL = async (quicksightSettings: QuicksightEmbedSettings) => {
		const authInfo = this.getAuthInfo('getQuicksightEmbedUrl', this.authStore.getToken);
		if (authInfo.isRefresh) {
			throw new Error('Needs Refresh');
		}

		if (authInfo.isValid) {
			return this.server.post(authInfo.path, JSON.stringify({ ...quicksightSettings }), {
				headers: { 'content-type': 'application/json', ...authInfo.headers },
			});
		}

		this.authStore.reset();
		throw new Error('Not valid');
	};

	readonly updateSummaryUpdateViewed = async (
		sid: string,
		summaryUpdateId: string,
		isRead: boolean,
		// @ts-expect-error - Legacy code, nullability not yet enforced
	): Promise<null | boolean | never[]> => {
		const authInfo = this.getAuthInfo('updateSummaryUpdateViewed', this.authStore.getToken);

		if (authInfo.isRefresh) {
			return null;
		}

		if (!authInfo.isValid) {
			console.warn('updateSummaryUpdateViewed, token is null, resetting authStore');
			this.authStore.reset();

			return [];
		}

		console.log(`updateSummaryUpdateViewed for sid code ${sid}`);

		try {
			const response = await this.server.post(authInfo.path, JSON.stringify({ sid, summaryUpdateId, isRead }), {
				headers: { 'content-type': 'application/json', ...authInfo.headers },
			});

			if (response?.status !== 200) {
				console.warn('An Error occurred', response);
				return false;
			}

			return true;
		} catch (err) {
			console.warn('An Error occurred', err);
		}
	};

	readonly submitInAppQaResults = async (
		docId: string,
		isTitleCorrect: boolean,
		taggingLoinc: string,
		description: string,
		level1: string,
		level2: string,
		level3: string,
		notes: string,
		// @ts-expect-error - Legacy code, nullability not yet enforced
	): Promise<null | never[] | Error> => {
		const authInfo = this.getAuthInfo('submitInAppQaResults', this.authStore.getToken);

		if (authInfo.isRefresh) {
			return null;
		}

		if (!authInfo.isValid) {
			console.warn('submitInAppQaResults, token is null, resetting authStore');
			this.authStore.reset();
			return [];
		}

		try {
			const response = await this.server.post(
				authInfo.path,
				JSON.stringify({
					docId,
					isTitleCorrect,
					taggingLoinc,
					description,
					level1,
					level2,
					level3,
					notes,
				}),
				{
					headers: { 'content-type': 'application/json', ...authInfo.headers },
				},
			);

			if (response?.status !== 200) {
				console.warn('An Error occurred', response);
				throw new Error(response.statusText);
			}
		} catch (err) {
			console.warn('An Error occurred', err);
			throw err;
		}
	};

	readonly getInAppQaLoincsHierarchy = async (): Promise<null | AxiosResponse> => {
		const authInfo = this.getAuthInfo('getInAppQaLoincsHierarchy', this.authStore.getToken);

		if (authInfo.isRefresh) {
			return null;
		}

		if (!authInfo.isValid) {
			console.warn('getInAppQaLoincsHierarchy, token is null, resetting authStore');
			this.authStore.reset();
			return null;
		}

		return this.server.post(authInfo.path, JSON.stringify({}), {
			headers: { 'content-type': 'application/json', ...authInfo.headers },
		});
	};

	readonly getScheduleAppointments = async (
		requestPayload: SchedulePageSearchRequestPayload,
	): Promise<null | AxiosResponse<SchedulePageSearchResponsePayload>> => {
		const authInfo = this.getAuthInfo('getScheduleAppointments', this.authStore.getToken);

		if (authInfo.isRefresh) {
			return null;
		}

		if (!authInfo.isValid) {
			console.warn('getScheduleAppointments, token is null, resetting authStore');
			this.authStore.reset();
			return null;
		}

		return this.server.post<SchedulePageSearchResponsePayload>(authInfo.path, JSON.stringify(requestPayload), {
			headers: { 'content-type': 'application/json', ...authInfo.headers },
		});
	};

	readonly getAdjacentAppointments = async (
		requestPayload: AdjacentAppointmentsRequestPayload,
	): Promise<null | AxiosResponse<AdjacentAppointmentsResponsePayload>> => {
		const authInfo = this.getAuthInfo('getAdjacentAppointments', this.authStore.getToken);

		if (authInfo.isRefresh) {
			return null;
		}

		if (!authInfo.isValid) {
			console.warn('getAdjacentAppointments, token is null, resetting authStore');
			this.authStore.reset();
			return null;
		}

		return this.server.post<AdjacentAppointmentsResponsePayload>(authInfo.path, JSON.stringify(requestPayload), {
			headers: { 'content-type': 'application/json', ...authInfo.headers },
		});
	};

	readonly getPatients = async (
		requestPayload: PatientSelectorRequestPayload,
	): Promise<null | AxiosResponse<PatientSelectorResponsePayload>> => {
		const authInfo = this.getAuthInfo('getPatients', this.authStore.getToken);

		if (authInfo.isRefresh) {
			return null;
		}

		if (!authInfo.isValid) {
			console.warn('getScheduleAppointments, token is null, resetting authStore');
			this.authStore.reset();
			return null;
		}

		return this.server.post<PatientSelectorResponsePayload>(authInfo.path, JSON.stringify(requestPayload), {
			headers: { 'content-type': 'application/json', ...authInfo.headers },
		});
	};

	readonly getGroupPractices = async (
		requestPayload: SelectedGroupPracticeRequestPayload,
	): Promise<null | AxiosResponse<SelectedGroupPracticeResponsePayload>> => {
		const authInfo = this.getAuthInfo('getGroupPractices', this.authStore.getToken);

		if (authInfo.isRefresh) {
			return null;
		}

		if (!authInfo.isValid) {
			console.warn('getGroupPractices, token is null, resetting authStore');
			this.authStore.reset();
			return null;
		}

		return this.server.post<SelectedGroupPracticeResponsePayload>(authInfo.path, JSON.stringify(requestPayload), {
			headers: { 'content-type': 'application/json', ...authInfo.headers },
		});
	};

	readonly getPatientLookup = async (
		requestPayload: PatientLookupRequestPayload,
	): Promise<null | AxiosResponse<PatientLookupResponsePayload>> => {
		const authInfo = this.getAuthInfo('getPatientLookup', this.authStore.getToken);

		if (authInfo.isRefresh) {
			return null;
		}

		if (!authInfo.isValid) {
			console.warn('getPatientLookup, token is null, resetting authStore');
			this.authStore.reset();
			return null;
		}

		return this.server.post<PatientLookupResponsePayload>(authInfo.path, JSON.stringify(requestPayload), {
			headers: { 'content-type': 'application/json', ...authInfo.headers },
		});
	};

	readonly getPatientDemographics = async (
		requestPayload: PatientDemographicsRequestPayload,
	): Promise<null | AxiosResponse<PatientDemographicsResponsePayload>> => {
		const authInfo = this.getAuthInfo('getPatientDemographics', this.authStore.getToken);

		if (authInfo.isRefresh) {
			return null;
		}

		if (!authInfo.isValid) {
			console.warn('getPatientDemographics, token is null, resetting authStore');
			this.authStore.reset();
			return null;
		}

		return this.server.post<PatientDemographicsResponsePayload>(authInfo.path, JSON.stringify(requestPayload), {
			headers: { 'content-type': 'application/json', ...authInfo.headers },
		});
	};

	readonly getAthenaAppointmentDetails = async (
		requestPayload: GetAthenaAppointmentDetailsRequestPayload,
	): Promise<null | AxiosResponse<GetAthenaAppointmentDetailsResponsePayload>> => {
		const authInfo = this.getAuthInfo('getAthenaAppointmentDetails', this.authStore.getToken);

		if (authInfo.isRefresh) {
			return null;
		}

		if (!authInfo.isValid) {
			this.authStore.reset();
			return null;
		}

		return this.server.post<GetAthenaAppointmentDetailsResponsePayload>(authInfo.path, JSON.stringify(requestPayload), {
			headers: { 'content-type': 'application/json', ...authInfo.headers },
		});
	};
}

let maybeApiGatewayServer: ApiGatewayServer | null = null;

function createApiGatewayServer(): ApiGatewayServer {
	const apiGatewayServer = new ApiGatewayServer();

	apiGatewayServer.server.interceptors.response.use(
		getSuccessHandler(),
		getErrorHandler(apiGatewayServer.server, getOrCreateApiSessionsServer()),
	);

	return apiGatewayServer;
}

export function getOrCreateApiGateway(): ApiGatewayServer {
	maybeApiGatewayServer ??= createApiGatewayServer();
	return maybeApiGatewayServer;
}
